import { useState } from "react";
import {
  ExternalLink,
  FilePlus2,
  Building2,
  PanelLeft,
  ToggleRight,
  PlusCircle,
  ClipboardCopy,
  Check,
  CircleCheck,
  CircleAlert,
  Send,
  Loader2,
  Link2Off,
} from "lucide-react";
import { slackTestRemote } from "@/lib/agentcore";

/**
 * SlackConnect - a clean, guided in-app flow to connect a Slack Incoming
 * Webhook so NovaSynth can deliver briefs and battlecards to a channel.
 *
 * Used in Settings (and surfaced in onboarding). It:
 *   1. Walks the user through the current (2026) Slack steps to create an
 *      Incoming Webhook (create app from scratch -> name + pick workspace ->
 *      activate Incoming Webhooks -> add new webhook to workspace -> pick
 *      channel -> copy the hooks.slack.com URL).
 *   2. Validates the pasted URL looks like a real Slack webhook.
 *   3. Test-before-confirm gating: once a valid URL is pasted, the user must
 *      send a test message FIRST. Confirming the connection only unlocks after
 *      that test succeeds. Editing the URL re-locks the gate.
 *
 * How the test uses the just-pasted URL
 * -------------------------------------
 * The backend test path (mode:"slack", op:"test") reads the webhook SERVER-SIDE
 * from the saved profile (never from the request body). So before calling
 * slackTestRemote we persist the pasted webhook just-in-time via onSave(). The
 * final "Confirm connection" button is therefore a confirmation that the now-saved
 * webhook actually delivered, not a separate write.
 *
 * Security: the webhook is a secret. We store it via the existing profile save,
 * never log it, and mask the middle when showing the connected state.
 */

/** A Slack incoming webhook looks like https://hooks.slack.com/services/T.../B.../xxxx */
const SLACK_WEBHOOK_RE = /^https:\/\/hooks\.slack\.com\/services\/[A-Za-z0-9/_-]+$/;

export function isValidSlackWebhook(url: string): boolean {
  return SLACK_WEBHOOK_RE.test(url.trim());
}

/** Mask a webhook for display so the secret tail is never shown in full. */
function maskWebhook(url: string): string {
  const m = url.match(/^https:\/\/hooks\.slack\.com\/services\/(.+)$/);
  if (!m) return "Connected";
  const tail = m[1];
  return `hooks.slack.com/services/${tail.slice(0, 4)}…${tail.slice(-4)}`;
}

interface Step {
  Icon: typeof ToggleRight;
  title: string;
  body: string;
  /** Optional plain-English helper note (e.g. what a "workspace" is). */
  note?: string;
  /** Optional inline link button rendered under the step body. */
  link?: { href: string; label: string };
}

const STEPS: Step[] = [
  {
    Icon: ExternalLink,
    title: "Open the Slack apps page",
    body: "Head to the Slack apps page where you create and manage apps. It opens in a new tab so you can follow along here.",
    link: { href: "https://api.slack.com/apps", label: "Open the Slack apps page" },
  },
  {
    Icon: FilePlus2,
    title: 'Click "Create New App", then choose "From scratch"',
    body: 'Pick "From scratch" (not "From a manifest"). This is the simplest path and is all NovaSynth needs.',
  },
  {
    Icon: Building2,
    title: "Name the app and pick your workspace, then Create App",
    body: 'Enter an App Name such as "NovaSynth" and select your Slack workspace. Then click "Create App".',
    note: "Your workspace is your Slack team or organization (the Slack you log into for work). The app is tied to this workspace and cannot be moved later, so pick the workspace where you want to receive briefs.",
  },
  {
    Icon: PanelLeft,
    title: 'Open "Incoming Webhooks"',
    body: 'On the app\'s Basic Information screen, look in the left sidebar and click "Incoming Webhooks".',
  },
  {
    Icon: ToggleRight,
    title: 'Turn on "Activate Incoming Webhooks"',
    body: "Toggle it ON. It starts in the OFF position, so this step is easy to miss.",
  },
  {
    Icon: PlusCircle,
    title: 'Click "Add New Webhook to Workspace"',
    body: 'Choose the channel you want NovaSynth to post to, then click "Allow".',
  },
  {
    Icon: ClipboardCopy,
    title: "Copy the Webhook URL and paste it below",
    body: "Slack shows a generated Webhook URL that starts with https://hooks.slack.com/services/. Copy it and paste it into the field below.",
  },
];

interface SlackConnectProps {
  idToken: string;
  /** Current saved webhook (from the profile). Empty string = not connected. */
  value: string;
  /** Persist a new webhook value to the account profile (parent owns the save). */
  onSave: (webhook: string) => Promise<void> | void;
  /** Optional: compact rendering for the onboarding card. */
  compact?: boolean;
}

type TestState =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "ok"; url: string }
  | { kind: "error"; message: string };

export function SlackConnect({ idToken, value, onSave, compact = false }: SlackConnectProps) {
  const [draft, setDraft] = useState(value);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [test, setTest] = useState<TestState>({ kind: "idle" });

  const connected = isValidSlackWebhook(value);
  const draftTrimmed = draft.trim();
  const draftValid = isValidSlackWebhook(draftTrimmed);
  const dirty = draftTrimmed !== value.trim();

  // The gate: Confirm only unlocks after a successful test of THIS exact URL.
  const testedOk = test.kind === "ok" && test.url === draftTrimmed;
  const showInvalid = draftTrimmed.length > 0 && !draftValid;

  /** Edits to the URL re-lock the gate: a changed URL needs a fresh test. */
  function onDraftChange(next: string) {
    setDraft(next);
    setTest({ kind: "idle" });
    setConfirmed(false);
  }

  /**
   * Send a test message. The backend reads the webhook server-side from the saved
   * profile, so we persist the pasted URL just-in-time first, then test it.
   */
  async function sendTest() {
    if (!draftValid || busy || test.kind === "sending") return;
    setBusy(true);
    setTest({ kind: "sending" });
    try {
      // Persist-for-test: the test cannot read the webhook from the request body.
      if (dirty) await onSave(draftTrimmed);
      const res = await slackTestRemote(idToken);
      if (res.ok) {
        setTest({ kind: "ok", url: draftTrimmed });
      } else {
        setTest({
          kind: "error",
          message:
            "We couldn't reach your Slack channel. Double-check the webhook URL is correct and still active, then try again.",
        });
      }
    } catch {
      setTest({
        kind: "error",
        message:
          "Something went wrong sending the test. Please check the webhook URL and try again.",
      });
    } finally {
      setBusy(false);
    }
  }

  /** Confirm the connection. The webhook is already saved (from the test); this just
   *  acknowledges the verified, active connection. */
  async function confirm() {
    if (!testedOk || busy) return;
    setBusy(true);
    try {
      // Idempotent re-save in case the profile was not yet persisted.
      if (dirty) await onSave(draftTrimmed);
      setConfirmed(true);
      setTimeout(() => setConfirmed(false), 2000);
    } finally {
      setBusy(false);
    }
  }

  async function disconnect() {
    if (busy) return;
    setBusy(true);
    setTest({ kind: "idle" });
    setConfirmed(false);
    try {
      await onSave("");
      setDraft("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Header + connection state */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-[#4A154B]/40 border border-[#4A154B]/30 flex items-center justify-center shrink-0">
            <SlackGlyph className="w-4 h-4" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-200">Connect Slack</p>
            <p className="text-[11px] text-slate-500 leading-tight mt-0.5">
              Deliver briefs and battlecards straight to a channel.
            </p>
          </div>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-semibold ${
            connected
              ? "border-emerald-700/40 bg-emerald-500/10 text-emerald-400"
              : "border-[#1a2640] bg-white/5 text-slate-500"
          }`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-emerald-400" : "bg-slate-600"}`} />
          {connected ? "Connected" : "Not connected"}
        </span>
      </div>

      {connected && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-emerald-800/30 bg-emerald-950/15 px-3.5 py-2.5">
          <span className="font-mono text-[11px] text-emerald-300/90 truncate" title="Your webhook is saved securely">
            {maskWebhook(value)}
          </span>
          <button
            type="button"
            onClick={disconnect}
            disabled={busy}
            className="inline-flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-red-400 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
          >
            <Link2Off className="w-3.5 h-3.5" />
            Disconnect
          </button>
        </div>
      )}

      {/* Guided steps */}
      <div className="rounded-xl border border-[#1a2640] bg-[#070d1a] p-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
            {connected ? "Reconnect in 7 steps" : "Set it up in 7 steps"}
          </p>
          <a
            href="https://api.slack.com/apps"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg border border-[#1a2640] bg-white/5 px-2.5 py-1.5 text-[11px] font-semibold text-[#22d3ee] hover:bg-[#22d3ee]/10 hover:border-[#22d3ee]/30 transition-colors cursor-pointer"
          >
            Open Slack apps
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <ol className={`space-y-3 ${compact ? "max-h-none" : ""}`}>
          {STEPS.map((s, i) => (
            <li key={s.title} className="flex items-start gap-2.5">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-[#1a2640] bg-[#0d1424] text-[#22d3ee]">
                <s.Icon className="w-3.5 h-3.5" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="text-[13px] font-medium text-slate-200 leading-snug">
                  <span className="text-slate-500 font-semibold mr-1.5">{i + 1}.</span>
                  {s.title}
                </p>
                <p className="text-[11px] text-slate-500 leading-relaxed mt-0.5">{s.body}</p>
                {s.note && (
                  <p className="mt-1.5 rounded-lg border border-[#1a2640] bg-[#0b1322] px-2.5 py-1.5 text-[11px] leading-relaxed text-slate-400">
                    {s.note}
                  </p>
                )}
                {s.link && (
                  <a
                    href={s.link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1.5 inline-flex items-center gap-1.5 rounded-lg border border-[#1a2640] bg-white/5 px-2.5 py-1.5 text-[11px] font-semibold text-[#22d3ee] hover:bg-[#22d3ee]/10 hover:border-[#22d3ee]/30 transition-colors cursor-pointer"
                  >
                    {s.link.label}
                    <ExternalLink className="w-3 h-3" aria-hidden="true" />
                  </a>
                )}
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Paste + save */}
      <div>
        <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-2">
          Paste your webhook URL
        </label>
        <input
          type="url"
          inputMode="url"
          autoComplete="off"
          spellCheck={false}
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          placeholder="https://hooks.slack.com/services/…"
          aria-invalid={showInvalid}
          className={`w-full px-3.5 py-2.5 bg-[#020617] border rounded-xl text-slate-200 placeholder-slate-700 text-xs font-mono focus:outline-none focus:ring-1 transition-all duration-200 ${
            showInvalid
              ? "border-red-700/50 focus:border-red-600/60 focus:ring-red-600/30"
              : "border-[#1a2640] hover:border-[#263350] focus:border-brand-600/50 focus:ring-brand-600/30"
          }`}
        />
        {showInvalid ? (
          <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-red-400/90">
            <CircleAlert className="w-3 h-3 shrink-0" />
            That doesn't look like a Slack webhook. It should start with https://hooks.slack.com/services/
          </p>
        ) : (
          <p className="mt-1.5 text-[11px] text-slate-600">
            Stored securely on your account and used only to deliver your briefs. We never post anything you don't ask for.
          </p>
        )}

        {/* Gating: send a test first; confirm only unlocks after it succeeds. */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {!testedOk && (
            <button
              type="button"
              onClick={sendTest}
              disabled={!draftValid || busy}
              title={
                !draftValid
                  ? "Paste a valid webhook URL first"
                  : "Send a test message to your Slack channel"
              }
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-500 hover:to-brand-400 px-4 py-2 text-sm font-semibold text-white transition-all shadow-glow-brand-sm disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer min-h-[40px]"
            >
              {test.kind === "sending" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {test.kind === "sending" ? "Sending test…" : "Send test message"}
            </button>
          )}

          {testedOk && (
            <button
              type="button"
              onClick={confirm}
              disabled={busy}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 px-4 py-2 text-sm font-semibold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer min-h-[40px]"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : confirmed ? (
                <Check className="w-4 h-4" />
              ) : (
                <CircleCheck className="w-4 h-4" />
              )}
              {confirmed ? "Connection confirmed" : "Confirm connection"}
            </button>
          )}
        </div>

        {/* Test result */}
        {test.kind === "ok" && (
          <p className="mt-2.5 flex items-center gap-1.5 text-[12px] font-medium text-emerald-400">
            <CircleCheck className="w-3.5 h-3.5 shrink-0" />
            Test message sent - check your Slack channel, then confirm the connection.
          </p>
        )}
        {test.kind === "error" && (
          <p className="mt-2.5 flex items-start gap-1.5 text-[12px] text-amber-300">
            <CircleAlert className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            {test.message}
          </p>
        )}
      </div>
    </div>
  );
}

/** Inline Slack glyph (brand-accurate). Not an emoji. */
function SlackGlyph({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#E01E5A" aria-hidden="true">
      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
    </svg>
  );
}
